import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
	Component,
	computed,
	ContentChild,
	input,
	OnInit,
	output,
	signal,
	TemplateRef
} from '@angular/core';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { groupBy, GroupResult } from '@progress/kendo-data-query';
import { DescriptiveSelectItem } from './descriptive-select-item';

const DEFAULT_ITEM_ID = '__default__';
const FILTER_THRESHOLD = 12;

/**
 * A Kendo-based select dropdown component with support for templated views.
 * This component allows rich item rendering with titles, descriptions, and icons.
 *
 * Usage example:
 * ```html
 * <dashboard-descriptive-select-field
 *   [items]="myItems"
 *   [selectedItem]="currentSelection"
 *   (selectionChange)="onSelectionChange($event)"
 *   [placeholder]="'Select an option...'">
 *
 *   <!-- Optional custom item template -->
 *   <ng-template #itemTemplate let-dataItem>
 *     <div>{{ dataItem.title }}</div>
 *   </ng-template>
 *
 *   <!-- Optional custom value template -->
 *   <ng-template #valueTemplate let-dataItem>
 *     <div>{{ dataItem.title }}</div>
 *   </ng-template>
 * </dashboard-descriptive-select-field>
 * ```
 */
@Component({
	selector: 'dashboard-descriptive-select-field',
	standalone: true,
	imports: [
		DropDownsModule,
		NgClass,
		NgTemplateOutlet
	],
	templateUrl: './dashboard-descriptive-select-field.widget.html',
	styleUrls: ['./dashboard-descriptive-select-field.widget.scss'],
})
export class DashboardDescriptiveSelectFieldWidget implements OnInit {

	/** The initially selected item, if any. */
	public readonly selectedItem = input<DescriptiveSelectItem | undefined>(undefined);

	/** The set of items in the list. */
	public readonly items = input.required<DescriptiveSelectItem[]>();

	/**
	 * If true or if items > 12, then the dropdown will contain a text area to filter the list.
	 * Default is true.
	 */
	public readonly filterable = input(true);

	/**
	 * Placeholder text when no item is selected.
	 * If provided, allows unselecting an item.
	 */
	public readonly placeholder = input<string>('');

	/**
	 * Label text displayed above the dropdown (Bootstrap form-floating style).
	 */
	public readonly label = input<string>('');

	/**
	 * Optional custom icon CSS classes for the input-group icon.
	 */
	public readonly customIcon = input<string>('');

	/**
	 * If true, shows a spinning loader icon.
	 */
	public readonly saving = input(false);

	/**
	 * Error message to display. When present, shows an error icon.
	 */
	public readonly error = input<string>('');

	/**
	 * If true, shows the subtext in the selected value display.
	 * Default is true.
	 */
	public readonly showSubTextInValue = input(true);

	/**
	 * Sort the list by the attribute passed in.
	 */
	public readonly sortBy = input<'uniqueId' | 'text' | 'customSort'>('text');

	/**
	 * If false, the items will not be sorted and will maintain their original order.
	 * Default is true.
	 */
	public readonly enableSorting = input(true);

	/**
	 * Group names are sorted alphabetically by default. Use groupSortValues to specifically
	 * force the order of one or more groups.
	 */
	public readonly groupSortValues = input<{ [index: string]: number } | undefined>(undefined);

	/**
	 * If true, then the select field will be disabled.
	 */
	public readonly disabled = input(false);

	/**
	 * If the select field has no items, this message will be displayed.
	 */
	public readonly missingDataMessage = input<string>("No items to select");

	/**
	 * Optional CSS class to apply to the dropdown.
	 */
	public readonly cssClass = input<string>('');

	/**
	 * When the selection is changed by the user, this event will be raised to indicate
	 * the new selected value.
	 */
	public readonly selectionChange = output<DescriptiveSelectItem | undefined>();

	/** Custom template for rendering items in the dropdown list */
	@ContentChild('itemTemplate', { read: TemplateRef }) itemTemplate?: TemplateRef<{ $implicit: DescriptiveSelectItem }>;

	/** Custom template for rendering the selected value */
	@ContentChild('valueTemplate', { read: TemplateRef }) valueTemplate?: TemplateRef<{ $implicit: DescriptiveSelectItem }>;

	public isFilterable = computed(() => this.filterable() && this.items().length > FILTER_THRESHOLD);

	private groupable = signal(false);

	// Generate unique ID for the dropdown instance
	public readonly uniqueId = signal(Math.random().toString(36).substring(2, 9));

	public setup = computed(() => {
		const setup = this.setDataAndSortItems();
		return {
			sortedItems: setup.sortedItems,
			data: signal<GroupResult[] | DescriptiveSelectItem[]>(setup.data),
		};
	});

	defaultItem: DescriptiveSelectItem = {
		uniqueId: DEFAULT_ITEM_ID,
		text: '',
	};

	ngOnInit(): void {
		if (!this.items()) throw new Error(`Missing required attribute: items`);

		// Determine if we need grouping
		this.groupable.set(false);
		for (const item of this.items()) {
			if (item.groupName) {
				this.groupable.set(true);
				break;
			}
		}

		// Add the default selection if placeholder is provided
		if (this.placeholder()) {
			this.defaultItem = {
				uniqueId: DEFAULT_ITEM_ID,
				text: this.placeholder(),
			};
		}
	}

	setDataAndSortItems() {
		const sortValues = this.groupSortValues() || {};

		// If sorting is disabled, use original order
		let newItems: DescriptiveSelectItem[];
		if (!this.enableSorting()) {
			newItems = [...this.items()];
		} else {
			// Sort items based on sortBy and grouping
			newItems = this.items().sort((i1, i2) => {
				// First sort by group if grouping is enabled
				if (this.groupable() && i1.groupName !== i2.groupName) {
					const i1s = sortValues[i1.groupName ?? ''] ?? Number.MAX_SAFE_INTEGER;
					const i2s = sortValues[i2.groupName ?? ''] ?? Number.MAX_SAFE_INTEGER;
					if (i1s === i2s) return (i1.groupName ?? '') > (i2.groupName ?? '') ? 1 : -1;
					else return i1s > i2s ? 1 : -1;
				}
				// Then sort by the specified field
				else if (this.sortBy() === 'text') {
					if (i1.text !== i2.text) {
						return i1.text > i2.text ? 1 : -1;
					}
				}
				else if (this.sortBy() === 'customSort') {
					if (i1.customSort && i2.customSort && i1.customSort !== i2.customSort) {
						return i1.customSort > i2.customSort ? 1 : -1;
					}
				}
				else if (this.sortBy() === 'uniqueId') {
					if (i1.uniqueId && i2.uniqueId && i1.uniqueId !== i2.uniqueId) {
						return i1.uniqueId > i2.uniqueId ? 1 : -1;
					}
				}
				return 0;
			});
		}

		// If grouping, handle it
		if (this.groupable()) {
			for (const item of newItems) {
				item.groupName = item.groupName ?? '?';
			}

			return {
				sortedItems: newItems,
				data: groupBy(newItems, [{ field: 'groupName' }]),
			};
		}
		else {
			return {
				data: newItems,
				sortedItems: newItems,
			};
		}
	}

	public handleFilter(query: string): void {
		const predicate = (item: DescriptiveSelectItem) => {
			const searchText = `${item.text} ${item.subText ?? ''}`.toLowerCase();
			return searchText.indexOf(query.toLowerCase()) >= 0;
		};

		if (this.groupable()) {
			this.setup().data.set(groupBy(this.setup().sortedItems.filter(predicate), [{ field: "groupName" }]));
		}
		else {
			this.setup().data.set(this.setup().sortedItems.filter(predicate));
		}
	}

	public itemDisabled(itemArgs: { dataItem: DescriptiveSelectItem; index: number }) {
		return !!itemArgs.dataItem?.disabled;
	}

	public valueChangeEvent(selectedItem: DescriptiveSelectItem) {
		if (selectedItem.uniqueId === DEFAULT_ITEM_ID) {
			this.selectionChange.emit(undefined);
		} else {
			this.selectionChange.emit(selectedItem);
		}
	}

	/**
	 * Creates the display text for the selected value when no custom template is provided.
	 */
	public getValueDisplay(item: DescriptiveSelectItem | null): string {
		if (!item || item.uniqueId === DEFAULT_ITEM_ID) {
			return this.placeholder();
		}
		return item.text;
	}

	/**
	 * Creates the display text for items in the list when no custom template is provided.
	 */
	public getItemDisplay(item: DescriptiveSelectItem): string {
		let display = item.text;
		if (item.groupName) {
			display = `${item.groupName} → ${display}`;
		}
		return display;
	}
}
